import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { addToSessionObject, BOOKING_SESSION_KEYS } from "../utils";
import {
  SeatAssignment,
  SeatSelectionMode,
  SeatSelectionState,
} from "../types/service-types/SeatMapTypes";

const defaultState: SeatSelectionState = {
  mode: "manual",
  assignments: [],
  skipped: false,
};

const writeSession = (state: SeatSelectionState) => {
  addToSessionObject(BOOKING_SESSION_KEYS.SEAT_SELECTION, "mode", state.mode);
  addToSessionObject(
    BOOKING_SESSION_KEYS.SEAT_SELECTION,
    "assignments",
    state.assignments
  );
  addToSessionObject(
    BOOKING_SESSION_KEYS.SEAT_SELECTION,
    "skipped",
    state.skipped
  );
  addToSessionObject(
    BOOKING_SESSION_KEYS.SEAT_SELECTION,
    "updatedAt",
    state.updatedAt
  );
};

export const setSeatSelectionMode =
  (body: SeatSelectionMode) => async (dispatch: Dispatch) => {
    dispatch(seatSelectionMode(body));
  };

export const setSeatAssignments =
  (body: SeatAssignment[]) => async (dispatch: Dispatch) => {
    dispatch(seatAssignments(body));
  };

export const setSeatSelection =
  (body: SeatSelectionState) => async (dispatch: Dispatch) => {
    dispatch(seatSelection(body));
  };

export const clearSeatSelection = () => (dispatch: Dispatch) => {
  dispatch(resetSeatSelection());
};

const initialState = (): SeatSelectionState => {
  let sessionData: SeatSelectionState | null = null;

  try {
    const raw = (window as any).sessionStorage.getItem(
      BOOKING_SESSION_KEYS.SEAT_SELECTION
    );
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && Array.isArray(parsed.assignments)) {
      sessionData = parsed;
    }
  } catch (error) {
    sessionData = null;
    console.warn("There was a problem with sessionStorage (seat selection)", error);
  }

  return {
    ...defaultState,
    ...(sessionData || {}),
  };
};

const stateSlice = createSlice({
  name: "bookflight/seat-selection",
  initialState: initialState(),
  reducers: {
    seatSelectionMode: (
      state: SeatSelectionState,
      action: PayloadAction<SeatSelectionMode>
    ) => {
      state.mode = action.payload;
      state.skipped = action.payload === "later";
      if (action.payload === "later") {
        state.assignments = [];
      }
      state.updatedAt = new Date().toISOString();
      writeSession(state);
    },
    seatAssignments: (
      state: SeatSelectionState,
      action: PayloadAction<SeatAssignment[]>
    ) => {
      state.assignments = action.payload;
      state.skipped = false;
      state.updatedAt = new Date().toISOString();
      writeSession(state);
    },
    seatSelection: (
      state: SeatSelectionState,
      action: PayloadAction<SeatSelectionState>
    ) => {
      state.mode = action.payload.mode;
      state.assignments = action.payload.assignments;
      state.skipped = action.payload.skipped;
      state.updatedAt = action.payload.updatedAt || new Date().toISOString();
      writeSession(state);
    },
    resetSeatSelection: (state: SeatSelectionState) => {
      state.mode = defaultState.mode;
      state.assignments = defaultState.assignments;
      state.skipped = defaultState.skipped;
      state.updatedAt = undefined;
      try {
        (window as any).sessionStorage.removeItem(
          BOOKING_SESSION_KEYS.SEAT_SELECTION
        );
      } catch (error) {
        console.warn("clear seat selection session failed", error);
      }
    },
  },
});

export const {
  seatSelectionMode,
  seatAssignments,
  seatSelection,
  resetSeatSelection,
} = stateSlice.actions;

export default stateSlice.reducer;
